import { useCallback, useState } from 'react';
import { useSuspenseOIDCProvidersQuery } from '#api/oidc-providers.queries.ts';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import type { OIDCProvider } from '#dn-types/oidc-provider.ts';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { SelectProviderType } from '../SelectProviderType.tsx';
import { AdminIdentityProviderForm } from './admin/AdminIdentityProviderForm.tsx';
import { EndpointIdentityProviderForm } from './endpoint/EndpointIdentityProviderForm.tsx';

const ADMIN_CALLBACK_HOST = 'https://admin.defined.net';
const ENDPOINT_CALLBACK_HOST = 'https://auth.defined.net';
const PARAM_SCHEMA = { type: 'scalar', domainId: 'scalar' } as const;

type AddProps = {
  id: null;
  onClose: () => void;
  adminIdentityProvider: null;
  endpointIdentityProvider: null;
};

type AdminEditProps = {
  id: string;
  adminIdentityProvider: OIDCProvider;
  endpointIdentityProvider: null;
  onClose: () => void;
};

type EndpointEditProps = {
  id: string;
  adminIdentityProvider: null;
  endpointIdentityProvider: EndpointOIDC;
  onClose: () => void;
};

export function IdentityProviderFormWrapper({
  onClose,
  id,
  adminIdentityProvider,
  endpointIdentityProvider,
}: AddProps | AdminEditProps | EndpointEditProps) {
  const {
    data: { data: adminOIDCProviders },
  } = useSuspenseOIDCProvidersQuery();
  const [params] = useUrlParams(PARAM_SCHEMA);
  // Can't add another admin provider, but we can edit an existing one
  const disableAdminProvider = adminOIDCProviders.length > 0 && !adminIdentityProvider;
  // If we're editing an endpoint, default to endpoint type
  const [type, setType] = useState<'Admin' | 'Endpoint'>(
    endpointIdentityProvider ? 'Endpoint'
      // If they can't add an admin provider, default to endpoint
    : disableAdminProvider ? 'Endpoint'
      // Check query param
    : params.type?.toLowerCase() === 'endpoint' && !adminIdentityProvider ? 'Endpoint'
    : 'Admin'
  );
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const isEditing = id !== null;
  // Hoist name field out of endpoint form for design reasons
  const [endpointName, setEndpointName] = useState(endpointIdentityProvider ? endpointIdentityProvider.name : '');
  const [endpointNameError, setEndpointNameError] = useState<string | null>(null);

  const callbackURL = new URL(
    '/auth/oidc-callback',
    type === 'Admin' ? ADMIN_CALLBACK_HOST : ENDPOINT_CALLBACK_HOST
  ).toString();

  const handleTypeChange = useCallback((newType: 'Admin' | 'Endpoint') => {
    setType(newType);
    setErrMsg(null);
  }, []);

  return (
    <Spacer direction="vertical" size="wide">
      {errMsg ?
        <ErrorMessage>{errMsg}</ErrorMessage>
      : null}
      <h2 className="h4 mb-8">Connection details</h2>
      <p className="p-md">
        Enter your identity provider’s OIDC credentials to establish a secure connection. These settings control how
        Defined Networking communicates with your provider for authentication.
      </p>
      <Spacer direction="horizontal" stackWhenNarrow={true} size="wide">
        <SelectProviderType
          className="width-100"
          disabled={isEditing}
          defaultValue={type}
          onChange={handleTypeChange}
          disableAdminProvider={disableAdminProvider}
          adminProviderHint={'One per account'} // if user is oidc, means they have an admin provider
        />
        <Field
          label="Name"
          className="width-100"
          helpTip="Choose a name that will help you distinguish this provider if you ever add more than one."
        >
          <Input
            className="width-100"
            hasError={Boolean(endpointNameError)}
            disabled={type === 'Admin'}
            value={type === 'Endpoint' ? endpointName : 'Admin identity provider'}
            onChange={(e) => {
              setEndpointName(e.target.value);
            }}
          />
          {endpointNameError ?
            <ErrorMessage>{endpointNameError}</ErrorMessage>
          : null}
        </Field>
      </Spacer>

      <div className="width-100">
        <p className="label mt-0 mb-8">
          Sign-in redirect URL
          <HelpTip>Your SSO provider will need this in order to send us back the authentication information.</HelpTip>
        </p>

        <Snippet className={'m-0'}>{callbackURL}</Snippet>
      </div>

      {type === 'Admin' ?
        id && adminIdentityProvider ?
          <AdminIdentityProviderForm
            adminIdentityProviderId={id}
            onCancel={onClose}
            setGlobalErrorMsg={setErrMsg}
            defaultValues={adminIdentityProvider}
          />
        : <AdminIdentityProviderForm
            adminIdentityProviderId={null}
            defaultDomainId={params.domainId}
            onCancel={onClose}
            setGlobalErrorMsg={setErrMsg}
          />

      : id && endpointIdentityProvider ?
        <EndpointIdentityProviderForm
          providerName={endpointName}
          endpointIdentityProviderId={id}
          defaultValues={endpointIdentityProvider}
          defaultDomainId={null}
          onCancel={onClose}
          setGlobalErrorMsg={setErrMsg}
          setNameErrorMsg={setEndpointNameError}
        />
      : <EndpointIdentityProviderForm
          providerName={endpointName}
          endpointIdentityProviderId={null}
          defaultDomainId={params.domainId}
          onCancel={onClose}
          setGlobalErrorMsg={setErrMsg}
          setNameErrorMsg={setEndpointNameError}
        />
      }
    </Spacer>
  );
}
